import {
  getCart,
  getBrandCartExpand,
  updateItemsQuantities,
  updateBrandSelection as _updateBrandSelection,
} from '@monolith/legacy/modules/cart-2/service';
import { isAxiosError } from 'axios';
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import { Cart2, NotificationCallback, PurchasableAggregate } from '@monolith/legacy/modules/cart-2/types';
import { captureConvenienceException, capturePurchasingException } from '@core/plugins/sentry/helper';
import { throttle } from '@core/utilities/lodash-utils';
import { BrandDiscount, BrandDiscountContext } from '@monolith/legacy/types/api/brand-discount';
import { getPersonalDiscountWithUuid } from '@monolith/legacy/services/api/brand-discount';
import usePrices from '@core/composables/use-price';
import {
  getAllOutOfStockProducts,
  getBrandIdsFromCart,
  getBrandsFromCartWithoutProductsDetails,
  calculatePercentageMinimumReached,
} from '@monolith/legacy/modules/cart-2/utils';
import { StockAlert, createBackInStockAlert, getBackInStockAlerts } from '@monolith/legacy/services/back-in-stock/back-in-stock';
import i18n from '@monolith/legacy/services/i18n';
import { ToasterType } from '@ankorstore/design-system';
import { trackCartPreviewViewed } from '@monolith/legacy/services/analytics/segment-ecommerce-v2/track';
import { useNotifications } from './use-notifications';
import { productsCount } from './use-counters';
import { getBrandDataFromAggregate } from '../utilities/cart';
import { getCurrency } from '@modules/core/services/metas/currency';
import { EXCLUDED_NON_EU_COUNTRIES_CODES } from '@client/modules/shared/constants/countries';

// * Global state * //
const cart = ref<Cart2>(null);
const restockAlerts = ref<string[]>(null);
const discounts = ref<BrandDiscount[]>([]);
const shouldDisplayEmptyCart = ref<boolean>(false);

// Dedupes concurrent ensureCart() callers (e.g. a prefetch plus a fast click) onto a single fetchCart() request.
let pendingCartFetch: Promise<void> | null = null;

// Dependencies
const { createNotification, createGenericErrorNotification } = useNotifications();

const setCartState = (cartState: Cart2): void => {
  cart.value = cartState;
  shouldDisplayEmptyCart.value = !(cartState.totalItemCount > 0);
  productsCount.value = cartState.totalItemCount;
};

// The retailer has no active cart (new retailer, emptied cart, or a cart that was
// converted to an order at checkout). This is a normal state, not an error.
const setEmptyCartState = (): void => {
  cart.value = null;
  shouldDisplayEmptyCart.value = true;
  productsCount.value = 0;
};

// * quantities updates * //
// performQuantitiesUpdates is a throttled function that will update the cart with the pending quantities updates
let pendingQuantitiesUpdates: Record<string, number> = {};
const performQuantitiesUpdates = throttle(async (mergedQuantity) => {
  if (Object.keys(pendingQuantitiesUpdates).length === 0) {
    return;
  }
  // Snapshot what we send so a change arriving mid-request survives instead of being wiped on clear.
  const sentUpdates = { ...pendingQuantitiesUpdates };
  try {
    setCartState(await updateItemsQuantities(cart?.value?.id, sentUpdates, mergedQuantity));
  } catch (e) {
    if (!(isAxiosError(e) && !e.response)) {
      captureConvenienceException(new Error('Error updating item quantity', { cause: e }));
    }
    createGenericErrorNotification(true);
  } finally {
    // Drop only the keys we sent and only if unchanged; mid-flight changes stay for the trailing call.
    Object.keys(sentUpdates).forEach((itemId) => {
      if (pendingQuantitiesUpdates[itemId] === sentUpdates[itemId]) {
        delete pendingQuantitiesUpdates[itemId];
      }
    });
  }
}, 2000);

export function useCart() {
  const { formatPrice } = usePrices();
  const store = useStore();
  const retailer = store.getters.retailer;

  const isLoading = ref<number>(0);

  const fetchCart = async (): Promise<void> => {
    // No cart to fetch: skip the request and set the empty-cart state (an absent
    // cart is normal, not an error). Page-level rendering of the empty cart is
    // gated separately, so this only clears cart state and stops the request.
    if (!retailer.cart_uuid) {
      setEmptyCartState();
      return;
    }

    isLoading.value++;
    try {
      setCartState(await getCart(retailer.cart_uuid));
    } catch (e) {
      // A 404 means the cart no longer exists (stale cart_uuid after deletion or
      // checkout conversion): treat it as an empty cart rather than an error.
      if (isAxiosError(e) && e.response?.status === 404) {
        setEmptyCartState();
        return;
      }
      // A 401 means the session expired: the global reloadOn401 interceptor already
      // reloads the page to re-authenticate, so this is expected, not an error to report.
      if (isAxiosError(e) && e.response?.status === 401) {
        return;
      }
      capturePurchasingException(new Error('Error fetching cart on cart page', { cause: e }));
      throw e;
    } finally {
      isLoading.value--;
    }
  };

  const fetchPersonalBrandDiscounts = async (cart: Cart2): Promise<void> => {
    const brandIds = getBrandIdsFromCart(cart);
    try {
      discounts.value = await getPersonalDiscountWithUuid(BrandDiscountContext.cartPage, brandIds);
    } catch (e) {
      capturePurchasingException(e.message);
    }
  };

  const initCart = async (): Promise<void> => {
    // Todo: Check error handling at the component page level
    await fetchCart();

    if (!cart.value) {
      return;
    }
    fetchPersonalBrandDiscounts(cart.value);
    fetchRestockAlerts(getAllOutOfStockProducts(cart.value));

    // No longer used (maybe in the future if the savedForLater is no more fetched in the cart)
    // fetchCounts(retailer.uuid);
  };

  // * User actions * //

  const trackCartPreviewViewedWrapper = () => {
    trackCartPreviewViewed(retailer.id, getBrandsFromCartWithoutProductsDetails(cart.value) ?? []);
  };

  const updateItemQuantity = async (itemId: string, quantity: number, mergedQuantity = false): Promise<void> => {
    pendingQuantitiesUpdates[itemId] = quantity;
    await performQuantitiesUpdates(mergedQuantity);
  };

  const updateBrandSelection = async (aggregate: PurchasableAggregate, selected: boolean): Promise<void> => {
    try {
      setCartState(
        await _updateBrandSelection(
          cart.value.id,
          (cart.value.itemAggregates?.data ?? []).map((a) => ({
            ...a,
            selected: a.id === aggregate.id ? selected : a.selected,
          }))
        )
      );
    } catch (e) {
      capturePurchasingException(new Error('Error updating brands selection from cart', { cause: e }));
      createGenericErrorNotification(true);
    }
  };

  const ensureCart = async (): Promise<void> => {
    if (cart.value) {
      return;
    }
    if (!pendingCartFetch) {
      pendingCartFetch = fetchCart().finally(() => {
        pendingCartFetch = null;
      });
    }
    await pendingCartFetch;
  };

  // Selects aggregateId exclusively and deselects every other brand. The backend PATCH is additive, so the
  // full aggregate list must be sent (a partial payload would leave other brands selected).
  const selectOnlyBrand = async (aggregateId: string): Promise<boolean> => {
    try {
      await ensureCart();
      if (!cart.value) {
        return false;
      }

      let aggregates = cart.value.itemAggregates?.data ?? [];
      if (!aggregates.some((a) => a.id === aggregateId)) {
        // The shared cart ref can be stale relative to what the floating cart shows (it never writes to it).
        // ensureCart() is a no-op once cart.value is set, so re-read server truth directly before trusting the
        // list: a .map() payload can never add a missing entry, and sending it as-is would deselect every
        // other brand while never selecting the target one.
        await fetchCart();
        if (!cart.value) {
          return false;
        }
        aggregates = cart.value.itemAggregates?.data ?? [];
        if (!aggregates.some((a) => a.id === aggregateId)) {
          throw new Error('Target brand is missing from the cart after refetch; refusing to send a payload that would deselect every other brand');
        }
      }

      setCartState(
        await _updateBrandSelection(
          cart.value.id,
          aggregates.map((a) => ({
            ...a,
            selected: a.id === aggregateId,
          }))
        )
      );
      return true;
    } catch (e) {
      capturePurchasingException(new Error('Error selecting brand exclusively for checkout', { cause: e }));
      createGenericErrorNotification(true);
      return false;
    }
  };

  // Preserves pack count across the swap, not unit count - if unit_multiplier differs between
  // the two variants, the total unit count changes accordingly (accepted for MVP).
  const changeItemVariant = async (oldPurchasableItemId: string, newPurchasableItemId: string, quantity: number): Promise<void> => {
    try {
      setCartState(
        await updateItemsQuantities(cart.value.id, {
          [oldPurchasableItemId]: 0,
          [newPurchasableItemId]: quantity,
        })
      );
    } catch (e) {
      capturePurchasingException(new Error('Error changing item variant', { cause: e }));
      createGenericErrorNotification(true);
    }
  };

  const removeBrand = async (aggregate: PurchasableAggregate): Promise<void> => {
    try {
      const items = aggregate.items?.data ?? (await getBrandCartExpand(aggregate.brand.data.id))?.items?.data ?? [];
      setCartState(
        await updateItemsQuantities(cart.value.id, Object.fromEntries(items.map((item) => [item.purchasableItemID, 0])))
      );
    } catch (e) {
      capturePurchasingException(new Error('Error removing brand from cart', { cause: e }));
      createGenericErrorNotification(true);
    }
  };

  const showInternationalShippingReimbursement = (cartBrand: PurchasableAggregate): boolean => {
    const brandCountry = cartBrand?.brand?.data?.country?.isoCode;
    const retailerCountry = retailer?.country?.iso_code;

    const brandExcluded = EXCLUDED_NON_EU_COUNTRIES_CODES.includes(brandCountry);
    const retailerExcluded = EXCLUDED_NON_EU_COUNTRIES_CODES.includes(retailerCountry);

    const shouldShow = (brandExcluded && !retailerExcluded) || (!brandExcluded && retailerExcluded);

    return shouldShow;
  };

  const showBrandsNameShippingReimbursement = (items: PurchasableAggregate[]): Array<string> => {
    const brandsNames = [];

    const retailerCountry = retailer?.country?.iso_code;
    const retailerExcluded = EXCLUDED_NON_EU_COUNTRIES_CODES.includes(retailerCountry);

    if (retailerExcluded) {
      items.forEach((item) => {
        brandsNames.push(item.brand.data.name);
      });
    } else {
      items.forEach((item) => {
        const brandCountry = item?.brand?.data?.country?.isoCode;
        const brandExcluded = EXCLUDED_NON_EU_COUNTRIES_CODES.includes(brandCountry);

        if (brandExcluded) {
          brandsNames.push(item.brand.data.name);
        }
      });
    }

    return brandsNames;
  };

  const itemAggregates = computed(() => {
    return cart.value?.itemAggregates?.data;
  });

  const cartSelectionTotal = computed(() => formatPrice(cart?.value?.selectionTotalAmount, '$0.00'));

  const cartTotalAmount = computed(() => {
    return itemAggregates.value?.reduce(
      (acc, item) => {
        acc.amount += item.totalAmountAfterDiscount.amount;
        return acc;
      },
      { amount: 0, currency: itemAggregates.value[0]?.totalAmountAfterDiscount?.currency || getCurrency() }
    );
  });

  const formatCartTotalAmount = computed(() => formatPrice(cartTotalAmount.value, '$0.00'));

  const cartBrandsCount = computed(() => {
    return (
      cart?.value?.itemAggregates?.data?.filter((aggregate) => aggregate?.hasReachedBrandMinimumOrderValue && aggregate?.selected)
        .length || 0
    );
  });

  const getPercentageMinimumReached = (aggregate: PurchasableAggregate) => {
    const brand = getBrandDataFromAggregate(aggregate);
    return calculatePercentageMinimumReached({
      totalAmount: aggregate.totalAmountAfterDiscount,
      minAmount: brand.minimumOrderValue,
    });
  };
  // * Restock alerts * //

  const isStockAlertLoading = computed(() => restockAlerts.value === null);

  const fetchRestockAlerts = async (variantsIds: string[]): Promise<void> => {
    try {
      restockAlerts.value = (await getBackInStockAlerts(variantsIds, 'uuid')).map(
        (alert: StockAlert) => alert.product_variant_uuid
      );
      return Promise.resolve();
    } catch (e) {
      capturePurchasingException(new Error('Error fetching restock alerts', { cause: e }));
      return Promise.reject(e);
    }
  };

  const createRestockAlert: NotificationCallback = async (item) => {
    try {
      restockAlerts.value.push((await createBackInStockAlert(item.id, 'uuid')).product_variant_uuid);
      createNotification(
        i18n.global.t('retailer.cart.product.restockAlertCreated.success', { product: item.name }),
        ToasterType.Success
      );
    } catch (e) {
      createNotification(
        i18n.global.t('retailer.cart.product.restockAlertCreated.error'),
        ToasterType.Error,
        i18n.global.t('retailer.cart.toaster.error.actionLabel'),
        () => createRestockAlert(item)
      );
      capturePurchasingException(new Error('Error creating restock alert', { cause: e }));
    }
  };

  return {
    cart,
    cartBrandsCount,
    cartSelectionTotal,
    changeItemVariant,
    createRestockAlert,
    discounts,
    ensureCart,
    getBrandDataFromAggregate,
    getPercentageMinimumReached,
    initCart,
    isLoading,
    isStockAlertLoading,
    restockAlerts,
    itemAggregates,
    removeBrand,
    selectOnlyBrand,
    setCartState,
    shouldDisplayEmptyCart,
    showInternationalShippingReimbursement,
    showBrandsNameShippingReimbursement,
    updateItemQuantity,
    updateBrandSelection,
    fetchCart,
    formatCartTotalAmount,
    trackCartPreviewViewedWrapper,
  };
}
